import { Switch } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { useAtom, useAtomValue } from 'jotai'
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProvidedSettingsNavConfigContext } from '../core'
import { Button, ConfirmationDialog, ConfirmationDialogRef, SearchFilter, SearchFilterProps } from '../rapid-ui'
import {
	AvailableExportTarget,
	availableExportTargetsAtom,
	exportConfigAtom,
	exportStatusAtom,
	exportTargetSearchQueryAtom,
	isLoadingAvailableTargetsAtom,
	isLoadingExportAtom,
	isLoadingMoreTargetsAtom,
	isResolvingExportTargetAtom,
	loadMoreExportTargetsAtom,
	selectedExportTargetAtom,
} from './state'

export type ExportConfiguratorProps = {
	isDrafting?: boolean
}

export function ExportConfigurator({ isDrafting }: ExportConfiguratorProps = {}) {
	const exportStatus = useAtomValue(exportStatusAtom)
	const availableExportTargets = useAtomValue(availableExportTargetsAtom)
	const isLoadingExport = useAtomValue(isLoadingExportAtom)
	const isResolvingExportTarget = useAtomValue(isResolvingExportTargetAtom)
	const isLoadingAvailableTargets = useAtomValue(isLoadingAvailableTargetsAtom)
	const [searchQuery, setSearchQuery] = useAtom(exportTargetSearchQueryAtom)
	const loadMore = useAtomValue(loadMoreExportTargetsAtom)
	const isLoadingMore = useAtomValue(isLoadingMoreTargetsAtom)

	return (
		<>
			{(isLoadingExport || isResolvingExportTarget)
				? <ExportBox state="loading" />
				: exportStatus.komboStatus === 'enabled'
				? (
					<ExportConfigEnabled
						availableExportTargets={availableExportTargets}
						availableExportTargetSearch={searchQuery}
						onAvailableExportTargetSearchChange={setSearchQuery}
						isLoading={isLoadingAvailableTargets}
						loadMore={loadMore}
						isLoadingMore={isLoadingMore}
						isDrafting={isDrafting}
					/>
				)
				: <ExportConfigNotEnabled />}
		</>
	)
}

function ExportConfigNotEnabled() {
	const { t } = useTranslation('editor')
	const { exportLinker } = useContext(ProvidedSettingsNavConfigContext)

	return (
		<ExportBox state="disabled">
			<div className="flex flex-col gap-2 text-center p-4">
				<div className="mx-auto mb-2">
					<NoConnectionIcon />
				</div>
				<p className="text-sm text-grey-700 font-semibold">
					{t('export.noAtsConnected')}
				</p>
				<p className="text-sm text-grey-600 mt-2">
					{t('export.dataStoredAtJobilla')}
				</p>
				<p className="text-xs text-grey-500 mt-4 bg-grey-50 p-4 rounded-lg">
					{t('export.connectAtsPrompt')} {exportLinker({
						children: t('export.organisationSettings'),
						className: 'text-brand-700 hover:text-brand-600 underline font-medium',
						target: '_blank',
					})}.
				</p>
			</div>
		</ExportBox>
	)
}

type ExportConfigEnabledProps = {
	availableExportTargets: AvailableExportTarget[]
	availableExportTargetSearch: string
	onAvailableExportTargetSearchChange: (search: string) => void
	isLoading: boolean
	loadMore: (() => void) | null
	isLoadingMore: boolean
	isDrafting?: boolean
}

function ExportConfigEnabled(props: ExportConfigEnabledProps) {
	const { t } = useTranslation('editor')
	const [exportConfig, setExportConfig] = useAtom(exportConfigAtom)
	const [selectedJob, setSelectedJob] = useAtom(selectedExportTargetAtom)
	const [, setSearchQuery] = useAtom(exportTargetSearchQueryAtom)
	const confirmationRef = useRef<ConfirmationDialogRef>(null)
	const syncDisqualifiedCandidates = exportConfig?.syncDisqualifiedCandidates ?? true

	const confirmExportChange = useCallback(
		async (): Promise<boolean> => {
			try {
				await confirmationRef.current?.confirm({
					title: 'Export integration is active',
					message:
						'This campaign is currently live. Changing or disabling the export integration will stop syncing new candidates '
						+ 'to the currently connected external ATS. Are you sure you want to continue?',
					confirmText: 'Confirm',
					cancelText: 'Cancel',
				})
				return true
			} catch {
				return false
			}
		},
		[],
	)

	const applyExportTargetChange = useCallback(
		async (target: AvailableExportTarget | null) => {
			if (!exportConfig) { return }

			if (!props.isDrafting && !!exportConfig?.exportTargetId) {
				const userConfirmed = await confirmExportChange()
				if (!userConfirmed) { return }
			}

			setSelectedJob(target)
			setExportConfig(prev => prev ? { ...prev, exportTargetId: target?.id ?? null } : prev)
			setSearchQuery('')
		},
		[exportConfig, setExportConfig, setSelectedJob, setSearchQuery, confirmExportChange],
	)

	const handleToggleSyncDisqualifiedCandidates = useCallback(
		async (nextValue: boolean) => {
			if (!exportConfig) { return }

			if (!props.isDrafting && !!exportConfig.exportTargetId) {
				const userConfirmed = await confirmExportChange()
				if (!userConfirmed) { return }
			}

			setExportConfig(prev => prev ? { ...prev, syncDisqualifiedCandidates: nextValue } : prev)
		},
		[exportConfig, setExportConfig, confirmExportChange, props.isDrafting],
	)

	return (
		<ExportBox state="enabled">
			<div className="w-full min-w-[450px]">
				<div className="mb-5">
					<DebouncedSearchFilter
						value={props.availableExportTargetSearch ?? ''}
						onChange={props.onAvailableExportTargetSearchChange}
						placeholder={t('export.searchJobs')}
					/>
				</div>

				{selectedJob
					? (
						<div className="mb-4">
							<div className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-2">
								{t('export.selectedJob')}
							</div>
							<SelectedJobCard
								job={selectedJob}
								onClear={() => applyExportTargetChange(null)}
							/>
						</div>
					)
					: (
						<div className="mb-4">
							<div className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-2">
								{t('export.exportStatus')}
							</div>
							<div className="p-3 rounded-lg border-2 border-grey-300 bg-grey-50">
								<div className="flex items-center gap-2">
									<div className="w-5 h-5 rounded-full bg-grey-300 flex items-center justify-center">
										<CloseIcon />
									</div>
									<span className="text-sm text-grey-600 font-medium">
										{t('export.exportDisabled')}
									</span>
								</div>
							</div>
						</div>
					)}

				{exportConfig?.exportTargetId
					? (
						<div className="mb-5 rounded-lg border border-grey-200 bg-grey-50 p-3">
							<div className="flex items-start gap-3">
								<Switch
									checked={syncDisqualifiedCandidates}
									onChange={nextValue => void handleToggleSyncDisqualifiedCandidates(nextValue)}
									className={ctl(`
										relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors
										${syncDisqualifiedCandidates ? 'bg-brand-600' : 'bg-grey-300'}
									`)}
								>
									<span className="sr-only">{t('export.syncDisqualifiedCandidates')}</span>
									<span
										aria-hidden="true"
										className={ctl(`
											inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow-sm transition-transform
											${syncDisqualifiedCandidates ? 'translate-x-[22px]' : 'translate-x-0.5'}
										`)}
									>
										<CheckIcon
											className={ctl(`
												h-3 w-3 text-brand-700 transition-opacity
												${syncDisqualifiedCandidates ? 'opacity-100' : 'opacity-0'}
											`)}
										/>
									</span>
								</Switch>
								<div>
									<span className="block text-sm font-medium text-grey-700">
										{t('export.syncDisqualifiedCandidates')}
									</span>
									<span className="mt-0.5 block text-xs text-grey-500">
										{t('export.syncDisqualifiedCandidatesHint')}
									</span>
								</div>
							</div>
						</div>
					)
					: null}

				<div className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-2">
					{selectedJob ? t('export.otherAvailableJobs') : t('export.availableJobs')}
				</div>

				<div
					className={ctl(`
						max-h-80 overflow-y-auto rounded-lg pr-1
						${props.isLoading && 'opacity-50 pointer-events-none'}
					`)}
				>
					{props.availableExportTargets.length === 0 && !props.isLoading
						? (
							<div className="flex items-center justify-center text-grey-500 text-sm">
								{t('export.noJobsFound')}
							</div>
						)
						: (
							<div className="space-y-2">
								{props.availableExportTargets
									.filter((t) => t.id !== selectedJob?.id)
									.map((target) => (
										<JobCard
											key={target.id}
											job={target}
											onSelect={() => applyExportTargetChange(target)}
										/>
									))}

								{props.loadMore && (
									<div className="mt-4 pb-2 flex justify-center">
										<Button
											type="subtle"
											size="short"
											onClick={props.loadMore}
											isLoading={props.isLoadingMore}
										>
											{t('export.loadMore')}
										</Button>
									</div>
								)}
							</div>
						)}
				</div>
			</div>
			<ConfirmationDialog ref={confirmationRef} />
		</ExportBox>
	)
}

type JobCardProps = {
	job: AvailableExportTarget
	onSelect: () => void
}

function JobCard({ job, onSelect }: JobCardProps) {
	const { t } = useTranslation('editor')
	return (
		<button
			type="button"
			onClick={onSelect}
			className={ctl(`
				w-full text-left p-3 rounded-lg border transition-all cursor-pointer
				hover:border-brand-300 hover:bg-brand-50/50
				border-grey-200 bg-white
			`)}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1 min-w-0">
					<div className="font-medium text-grey-900 text-sm truncate">
						{job.name || t('export.untitledJob')}
					</div>
					<div className="flex flex-wrap items-center gap-2 mt-1.5">
						{job.status && <JobStatusBadge status={job.status} />}
						{job.location && (
							<span className="text-xs text-grey-500 flex items-center gap-1">
								<LocationIcon />
								{job.location}
							</span>
						)}
						{job.location && job.employmentType && <span className="text-xs text-grey-400">·</span>}
						{job.employmentType && (
							<span className="text-xs text-grey-500">
								{job.employmentType}
							</span>
						)}
					</div>
				</div>
				<div className="shrink-0 w-5 h-5 rounded-full border-2 border-grey-300" />
			</div>
		</button>
	)
}

type SelectedJobCardProps = {
	job: AvailableExportTarget
	onClear: () => void
}

function SelectedJobCard({ job, onClear }: SelectedJobCardProps) {
	const { t } = useTranslation('editor')
	return (
		<div className="p-3 rounded-lg border-2 border-brand-500 bg-brand-50">
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1 min-w-0">
					<div className="font-semibold text-grey-900 text-sm">
						{job.name || t('export.untitledJob')}
					</div>
					<div className="flex flex-wrap items-center gap-2 mt-1.5">
						{job.status && <JobStatusBadge status={job.status} />}
						{job.location && (
							<span className="text-xs text-grey-600 flex items-center gap-1">
								<LocationIcon />
								{job.location}
							</span>
						)}
						{job.location && job.employmentType && <span className="text-xs text-grey-400">·</span>}
						{job.employmentType && (
							<span className="text-xs text-grey-600">
								{job.employmentType}
							</span>
						)}
					</div>
					{job.postUrl && (
						<a
							href={job.postUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-xs text-brand-600 hover:text-brand-700 underline mt-2 inline-flex items-center gap-1"
						>
							{t('export.viewJobPosting')}
							<ExternalLinkIcon />
						</a>
					)}
				</div>
				<button
					type="button"
					onClick={onClear}
					className="shrink-0 p-1 rounded hover:bg-brand-100 text-grey-500 hover:text-grey-700 transition-colors"
					title={t('export.removeSelection')}
					aria-label={t('export.removeSelection')}
				>
					<CloseIcon />
				</button>
			</div>
		</div>
	)
}

function JobStatusBadge({ status }: { status: string }) {
	const isOpen = status.toUpperCase() === 'OPEN'
	return (
		<span
			className={ctl(`
				inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium
				${isOpen ? 'bg-green-100 text-green-700' : 'bg-grey-100 text-grey-600'}
			`)}
		>
			{status}
		</span>
	)
}

function DebouncedSearchFilter(props: SearchFilterProps) {
	const [value, setValue] = useState(props.value)

	useEffect(() => {
		setValue(props.value)
	}, [props.value])

	const handleChange = useCallback((value: string) => {
		setValue(value)
		props.onChange(value)
	}, [props.onChange])

	return <SearchFilter {...props} value={value} onChange={handleChange} />
}

function ExportBox({ children, state }: { children?: ReactNode; state: 'enabled' | 'disabled' | 'loading' }) {
	const { t } = useTranslation('editor')
	const outerClassName = ctl(`
		max-w-[500px]
		w-[500px]
		bg-gradient-to-b
		${state === 'enabled' && 'from-brand-200 to-brand-300 ring-brand-400/50'}
	 	${state === 'disabled' && 'from-grey-200 to-grey-300 ring-grey-400/50'}
		${state === 'loading' && 'from-grey-200 to-grey-300 ring-grey-400/50'}
		ring rounded-lg shadow-lg p-1 w-full
	`)

	const innerClassName = ctl(`
		flex flex-col items-center justify-center gap-4 bg-white rounded-md p-4 h-full ring
		${state === 'enabled' && 'ring-brand-300/50'}
		${state === 'disabled' && 'ring-grey-300/50'}
		${state === 'loading' && 'ring-grey-300/50'}
		shadow-sm
	`)

	return (
		<div className={outerClassName}>
			<div className={innerClassName}>
				{state === 'loading'
					? (
						<div className="flex flex-col items-center justify-center gap-3 py-8">
							<LoadingSpinner />
							<div className="text-sm text-grey-600">{t('export.checkingExportStatus')}</div>
						</div>
					)
					: children}
			</div>
		</div>
	)
}

function LoadingSpinner() {
	return (
		<svg
			className="animate-spin h-6 w-6 text-brand-500"
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
		>
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path
				className="opacity-75"
				fill="currentColor"
				d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
			/>
		</svg>
	)
}

function NoConnectionIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="48"
			height="48"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="text-grey-400"
		>
			<path d="M16 16v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H19a2 2 0 0 1 2 2v4.34" />
			<line x1="2" y1="2" x2="22" y2="22" />
		</svg>
	)
}

function LocationIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="12"
			height="12"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
			<circle cx="12" cy="10" r="3" />
		</svg>
	)
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 16 16" fill="currentColor" className={className}>
			<path
				fillRule="evenodd"
				d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
				clipRule="evenodd"
			/>
		</svg>
	)
}

function CloseIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<line x1="18" y1="6" x2="6" y2="18" />
			<line x1="6" y1="6" x2="18" y2="18" />
		</svg>
	)
}

function ExternalLinkIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="12"
			height="12"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
			<polyline points="15 3 21 3 21 9" />
			<line x1="10" y1="14" x2="21" y2="3" />
		</svg>
	)
}
